import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Button, Card, Input, Modal, useToast, StatusBadge } from '../shared/UI';

export function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [counters, setCounters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'counter', counter_id: null });
  const { show, ToastContainer } = useToast();

  const loadData = async () => {
    const [u, c] = await Promise.all([
      api.get('/api/admin/users'),
      api.get('/api/counters')
    ]);
    setUsers(u);
    setCounters(c);
  };

  useEffect(() => { loadData(); }, []);

  const openModal = (user = null) => {
    if (user) {
      setEditing(user);
      setFormData({ username: user.username, password: '', role: user.role, counter_id: user.counter_id });
    } else {
      setEditing(null);
      setFormData({ username: '', password: '', role: 'counter', counter_id: null });
    }
    setShowModal(true);
  };

  const save = async () => {
    try {
      await api.post('/api/admin/users', { ...formData, id: editing?.id });
      show(editing ? 'User updated' : 'User created');
      setShowModal(false);
      loadData();
    } catch (err) {
      show(err.error || 'Failed to save user', 'error');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/api/admin/users/${id}`);
      show('User deleted');
      loadData();
    } catch (err) {
      show(err.error || 'Failed to delete user', 'error');
    }
  };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)' }}>User Management</h1>
          <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>Manage administrators and counter agents</p>
        </div>
        <Button onClick={() => openModal()} icon="＋">Add New User</Button>
      </div>

      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--gray-100)' }}>
              <th style={{ padding: '16px 20px', fontSize: 12, textTransform: 'uppercase', color: 'var(--gray-500)', letterSpacing: '0.05em' }}>Username</th>
              <th style={{ padding: '16px 20px', fontSize: 12, textTransform: 'uppercase', color: 'var(--gray-500)', letterSpacing: '0.05em' }}>Role</th>
              <th style={{ padding: '16px 20px', fontSize: 12, textTransform: 'uppercase', color: 'var(--gray-500)', letterSpacing: '0.05em' }}>Assigned Counter</th>
              <th style={{ padding: '16px 20px', fontSize: 12, textTransform: 'uppercase', color: 'var(--gray-500)', letterSpacing: '0.05em', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: i === users.length - 1 ? 'none' : '1px solid var(--gray-50)', background: i % 2 === 0 ? 'transparent' : 'var(--gray-50)' }}>
                <td style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--navy)' }}>{u.username}</td>
                <td style={{ padding: '16px 20px' }}>
                  <StatusBadge status={u.role === 'admin' ? 'Active' : 'Waiting'} label={u.role} />
                </td>
                <td style={{ padding: '16px 20px', color: 'var(--gray-600)' }}>
                  {u.role === 'counter' ? (u.counter_name || 'Unassigned') : '—'}
                </td>
                <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button variant="ghost" size="sm" onClick={() => openModal(u)}>✎</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(u.id)} style={{ color: 'var(--red)' }}>🗑</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit User' : 'Create User'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Input label="Username" value={formData.username} onChange={val => setFormData({ ...formData, username: val })} />
          <Input label={editing ? "New Password (leave blank to keep)" : "Password"} type="password" value={formData.password} onChange={val => setFormData({ ...formData, password: val })} />
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Role</label>
            <div style={{ display: 'flex', gap: 12 }}>
              {['admin', 'counter'].map(role => (
                <button key={role} onClick={() => setFormData({ ...formData, role })}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid',
                    background: formData.role === role ? 'var(--ice)' : 'transparent',
                    borderColor: formData.role === role ? 'var(--cobalt)' : 'var(--gray-100)',
                    color: formData.role === role ? 'var(--cobalt)' : 'var(--gray-500)',
                    fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize'
                  }}>
                  {role}
                </button>
              ))}
            </div>
          </div>

          {formData.role === 'counter' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Assign Counter</label>
              <select value={formData.counter_id || ''} onChange={e => setFormData({ ...formData, counter_id: e.target.value ? parseInt(e.target.value) : null })}
                style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--gray-100)', outline: 'none' }}>
                <option value="">Select a counter…</option>
                {counters.map(c => <option key={c.id} value={c.id}>{c.label} - {c.name}</option>)}
              </select>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <Button onClick={() => setShowModal(false)} variant="ghost" style={{ flex: 1 }}>Cancel</Button>
            <Button onClick={save} style={{ flex: 1 }}>Save User</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
